import React, { useState, useRef, useMemo } from "react";
import { motion, useInView } from "framer-motion";
import {
  Search,
  Calendar,
  User,
  Clock,
  BookOpen,
  Tag,
  Eye,
  MessageCircle,
} from "lucide-react";
import SecondaryNavbar from "../../components/common/SecondaryNavbar";
import SecondaryHero from "../../components/common/SecondaryHero";

const Blogs = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, threshold: 0.1 });
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Sample blog posts
  const blogPosts = [
    {
      id: 1,
      title: "Advancements in Drone Surveying for Environmental Conservation",
      excerpt:
        "Exploring how drone technology is revolutionizing environmental monitoring and conservation efforts with unprecedented precision.",
      image: "https://garudsurvey.com/wp-content/uploads/2023/12/2952-1.jpg",
      category: "drones",
      author: "Sarah Chen",
      date: "2024-03-15",
      readTime: "6 min read",
      views: 1247,
      comments: 18,
      tags: ["Drone Technology", "Environmental", "Conservation"],
      featured: true,
    },
    {
      id: 2,
      title: "GIS in Urban Planning: Building Smarter Cities",
      excerpt:
        "How geographic information systems are transforming urban development and creating more sustainable, efficient cities.",
      image: "https://static.wixstatic.com/media/5d1736_9d0a1e6346534321be2e7d92cc761981~mv2.jpg/v1/fill/w_980,h_564,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/5d1736_9d0a1e6346534321be2e7d92cc761981~mv2.jpg",
      category: "gis",
      author: "Rajesh Kumar",
      date: "2024-03-12",
      readTime: "8 min read",
      views: 892,
      comments: 12,
      tags: ["GIS Mapping", "Urban Planning", "Smart Cities"],
      featured: true,
    },
    {
      id: 3,
      title: "The Future of Spatial Data Analysis in 2024",
      excerpt:
        "Latest trends and technologies shaping the future of spatial data analysis and geographic information systems.",
      image: "https://media.licdn.com/dms/image/v2/D5612AQHFgPXrsHbGsQ/article-cover_image-shrink_720_1280/article-cover_image-shrink_720_1280/0/1720951063070?e=2147483647&v=beta&t=aOvM5xgvQ5zFEBGHmiwEgVt_eCsBsf1M5H3aVkRdVVk",
      category: "gis",
      author: "Mike Johnson",
      date: "2024-03-10",
      readTime: "5 min read",
      views: 1563,
      comments: 24,
      tags: ["Spatial Analysis", "Data Visualization", "GIS Technology"],
    },
    {
      id: 4,
      title: "Environmental Impact Assessment Best Practices",
      excerpt:
        "Comprehensive guide to conducting effective environmental impact assessments using modern GIS tools.",
      image: "https://cdn.exchangewire.com/wp-content/uploads/2022/01/environmental-protection-gfa675e86f_1920.jpg",
      category: "environment",
      author: "Emily Watson",
      date: "2024-03-08",
      readTime: "7 min read",
      views: 734,
      comments: 8,
      tags: ["Environmental", "Assessment", "GIS Tools"],
    },
    {
      id: 5,
      title: "Building Scalable Mapping Applications with React",
      excerpt:
        "Technical deep dive into creating high-performance mapping applications using React and modern web technologies.",
      image: "https://towardsdatascience.com/wp-content/uploads/2023/02/0Za8SFVwrLri4kHKe-scaled.jpg",
      category: "software",
      author: "Alex Thompson",
      date: "2024-03-05",
      readTime: "10 min read",
      views: 2105,
      comments: 31,
      tags: ["Web Development", "React", "Mapping Applications"],
    },
    {
      id: 6,
      title: "Mining Industry Transformation Through GIS",
      excerpt:
        "How GIS technology is driving efficiency and sustainability in the modern mining industry.",
      image: "https://edstechnologies.com/wp-content/uploads/2024/12/Future-of-gis-mine.jpg",
      category: "industry",
      author: "David Park",
      date: "2024-03-02",
      readTime: "6 min read",
      views: 567,
      comments: 6,
      tags: ["Mining", "Industry Insights", "GIS Applications"],
    },
  ];

  // Calculate dynamic counts using useMemo for optimization
  const { categories, popularTags, recentPosts } = useMemo(() => {
    // Calculate category counts
    const categoryCounts = blogPosts.reduce((acc, post) => {
      acc[post.category] = (acc[post.category] || 0) + 1;
      return acc;
    }, {});

    const categories = [
      { id: "all", name: "All Articles", count: blogPosts.length },
      { id: "gis", name: "GIS Technology", count: categoryCounts.gis || 0 },
      { id: "drones", name: "Drone Surveys", count: categoryCounts.drones || 0 },
      { id: "environment", name: "Environmental", count: categoryCounts.environment || 0 },
      { id: "software", name: "Software Development", count: categoryCounts.software || 0 },
      { id: "industry", name: "Industry Insights", count: categoryCounts.industry || 0 },
    ];

    // Extract all tags from blog posts and calculate their frequencies
    const allTags = blogPosts.flatMap(post => post.tags);
    const tagFrequency = allTags.reduce((acc, tag) => {
      acc[tag] = (acc[tag] || 0) + 1;
      return acc;
    }, {});

    // Get popular tags (sorted by frequency, then alphabetical)
    const popularTags = Object.entries(tagFrequency)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([tag]) => tag)
      .slice(0, 12); // Get top 12 tags

    // Get recent posts (sorted by date, most recent first)
    const recentPosts = [...blogPosts]
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 3);

    return { categories, popularTags, recentPosts };
  }, [blogPosts]);

  // Filter blog posts
  const filteredPosts = blogPosts.filter((post) => {
    const matchesCategory =
      selectedCategory === "all" || post.category === selectedCategory;
    const matchesSearch =
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.tags.some((tag) =>
        tag.toLowerCase().includes(searchQuery.toLowerCase())
      );
    return matchesCategory && matchesSearch;
  });

  const featuredPosts = filteredPosts.filter((post) => post.featured);
  const regularPosts = filteredPosts.filter((post) => !post.featured);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        duration: 0.6,
      },
    },
  };

  return (
    <div className='overflow-hidden'>
      <SecondaryNavbar />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        <SecondaryHero
          pageName="Insights & Updates"
          pageHead1="Maptech"
          pageHead2="News"
          pageHead3="&"
          pageHead4="Blogs"
          pageDesc="Stay updated with the latest in GIS technology, industry insights, and innovative solutions."
          bgImgUrl="https://sb.ecobnb.net/app/uploads/sites/3/2022/04/copertina-1.jpg"
        />

        {/* Blog Content */}
        <section ref={sectionRef} className="py-6 lg:py-12 tracking-wide">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-4 gap-8 lg:gap-12">
              {/* Main Content - 3 columns */}
              <div className="lg:col-span-3">
                {/* Search and Filters */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={
                    isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }
                  }
                  transition={{ duration: 0.6 }}
                  className="mb-12"
                >
                  <div className="flex flex-col lg:flex-row gap-6 mb-8">
                    {/* Search Bar */}
                    <div className="flex-1 relative">
                      <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        placeholder="Search articles..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-12 pr-4 py-4 bg-white border border-gray-300 rounded-2xl focus:ring-2 focus:ring-brandBlue focus:border-transparent transition-all duration-300"
                      />
                    </div>
                  </div>

                  {/* Category Filters */}
                  <div className="flex flex-wrap gap-3">
                    {categories.map((category) => (
                      <motion.button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`px-6 py-3 rounded-2xl font-semibold transition-all duration-300 ${
                          selectedCategory === category.id
                            ? "bg-brandBlue text-white shadow-lg"
                            : "bg-white text-gray-700 border border-gray-300 hover:border-brandBlue hover:text-brandBlue"
                        }`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {category.name} ({category.count})
                      </motion.button>
                    ))}
                  </div>
                </motion.div>

                {/* Featured Posts */}
                {featuredPosts.length > 0 && (
                  <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                    className="mb-12"
                  >
                    <h2 className="text-3xl font-bold text-primaryTextColor mb-8 font-lexend flex items-center">
                      <div className="w-3 h-3 bg-brandBlue rounded-full mr-3"></div>
                      Featured Articles ({featuredPosts.length})
                    </h2>

                    <div className="grid gap-8">
                      {featuredPosts.map((post, index) => (
                        <motion.article
                          key={post.id}
                          variants={itemVariants}
                          className="group bg-white rounded-3xl border border-gray-200 hover:border-brandBlue/30 transition-all duration-500 hover:shadow-2xl overflow-hidden"
                        >
                          <div className="lg:flex">
                            {/* Image */}
                            <div className="lg:w-2/5 relative overflow-hidden rounded-3xl">
                              <div
                                className="aspect-video lg:aspect-square lg:h-full bg-gray-100 bg-center bg-cover transition-transform duration-700 hover:scale-105"
                                style={{
                                  backgroundImage: `url("${
                                    post.image ||
                                    "https://www.ganatantrabharat.com/wp-content/uploads/2022/01/forests-report.jpg"
                                  }")`,
                                }}
                              ></div>

                              {/* Optional dark gradient overlay for readability */}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none"></div>

                              {/* Badge */}
                              <div className="absolute top-4 left-4">
                                <span className="px-3 py-1 bg-brandBlue text-white text-sm font-semibold rounded-full shadow-lg">
                                  Featured
                                </span>
                              </div>
                            </div>

                            {/* Content */}
                            <div className="lg:w-3/5 p-8">
                              <div className="flex items-center space-x-4 text-sm text-secondaryTextColor mb-4">
                                <div className="flex items-center space-x-1">
                                  <Calendar className="w-4 h-4" />
                                  <span>
                                    {new Date(post.date).toLocaleDateString()}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <Clock className="w-4 h-4" />
                                  <span>{post.readTime}</span>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <User className="w-4 h-4" />
                                  <span>{post.author}</span>
                                </div>
                              </div>

                              <h3 className="text-2xl lg:text-3xl font-bold text-primaryTextColor mb-4 font-lexend group-hover:text-brandBlue transition-colors duration-300">
                                {post.title}
                              </h3>

                              <p className="text-secondaryTextColor leading-relaxed mb-6 text-lg">
                                {post.excerpt}
                              </p>

                              {/* Tags */}
                              <div className="flex flex-wrap gap-2 mb-6">
                                {post.tags.map((tag, tagIndex) => (
                                  <span
                                    key={tagIndex}
                                    className="px-3 py-1 bg-gray-100 text-secondaryTextColor text-sm font-medium rounded-lg"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>

                              {/* Stats and CTA */}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4 text-sm text-secondaryTextColor">
                                  <div className="flex items-center space-x-1">
                                    <Eye className="w-4 h-4" />
                                    <span>{post.views.toLocaleString()}</span>
                                  </div>
                                  <div className="flex items-center space-x-1">
                                    <MessageCircle className="w-4 h-4" />
                                    <span>{post.comments}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.article>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Regular Posts Grid */}
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                >
                  <h2 className="text-3xl font-bold text-primaryTextColor mb-8 font-lexend flex items-center">
                    <div className="w-3 h-3 bg-brandGreen rounded-full mr-3"></div>
                    Latest Articles ({regularPosts.length})
                  </h2>

                  <div className="grid md:grid-cols-2 gap-8">
                    {regularPosts.map((post, index) => (
                      <motion.article
                        key={post.id}
                        variants={itemVariants}
                        className="group bg-white rounded-3xl border border-gray-200 hover:border-brandBlue/30 transition-all duration-500 hover:shadow-2xl overflow-hidden"
                      >
                        {/* Image */}
                        <div className="aspect-video relative overflow-hidden rounded-2xl">
                          <img
                            src={
                              post.image ||
                              "https://static.vecteezy.com/system/resources/previews/027/532/977/large_2x/an-asian-woman-goes-on-a-community-forest-survey-in-northern-thailand-to-study-the-biodiversity-of-the-forest-s-abundant-life-in-the-concept-of-a-forest-explorer-collecting-forest-hiking-photo.jpg"
                            }
                            alt={post.title || "Article Image"}
                            className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                          />

                          {/* Subtle overlay for better contrast on hover */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500"></div>

                          {/* Category Badge */}
                          <div className="absolute top-4 left-4">
                            <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-secondaryTextColor text-sm font-semibold rounded-full shadow-lg">
                              {categories.find(
                                (cat) => cat.id === post.category
                              )?.name || "General"}
                            </span>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="p-6">
                          <div className="flex items-center space-x-3 text-sm text-secondaryTextColor mb-3">
                            <div className="flex items-center space-x-1">
                              <Calendar className="w-4 h-4" />
                              <span>
                                {new Date(post.date).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Clock className="w-4 h-4" />
                              <span>{post.readTime}</span>
                            </div>
                          </div>

                          <h3 className="text-xl font-bold text-primaryTextColor mb-3 font-lexend group-hover:text-brandBlue transition-colors duration-300 line-clamp-2">
                            {post.title}
                          </h3>

                          <p className="text-secondaryTextColor leading-relaxed mb-4 line-clamp-3">
                            {post.excerpt}
                          </p>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 text-sm text-secondaryTextColor">
                              <User className="w-4 h-4" />
                              <span>{post.author}</span>
                            </div>
                          </div>
                        </div>
                      </motion.article>
                    ))}
                  </div>
                </motion.div>

                {/* Empty State */}
                {filteredPosts.length === 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-center py-16"
                  >
                    <div className="w-24 h-24 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
                      <Search className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-primaryTextColor mb-4">
                      No articles found
                    </h3>
                    <p className="text-secondaryTextColor max-w-md mx-auto">
                      Try adjusting your search criteria or browse all
                      categories to find what you're looking for.
                    </p>
                  </motion.div>
                )}
              </div>

              {/* Sidebar - 1 column */}
              <div className="lg:col-span-1">
                <div className="space-y-8">
                  {/* About Blog */}
                  <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    animate={
                      isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }
                    }
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="bg-white rounded-3xl border border-gray-200 p-6"
                  >
                    <h3 className="text-xl font-bold text-primaryTextColor mb-4 font-lexend">
                      About Our Blog
                    </h3>
                    <p className="text-secondaryTextColor leading-relaxed mb-4">
                      Stay updated with the latest insights, trends, and
                      innovations in GIS technology, drone surveys, and spatial
                      analysis.
                    </p>
                    <div className="text-sm text-secondaryTextColor">
                      <span className="font-semibold text-brandBlue">
                        {blogPosts.length} articles
                      </span>{" "}
                      across {categories.filter(c => c.id !== 'all').length} categories
                    </div>
                  </motion.div>

                  {/* Popular Tags */}
                  <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    animate={
                      isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }
                    }
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="bg-white rounded-3xl border border-gray-200 p-6"
                  >
                    <h3 className="text-xl font-bold text-primaryTextColor mb-4 font-lexend flex items-center">
                      <Tag className="w-5 h-5 mr-2 text-brandBlue" />
                      Popular Tags ({popularTags.length})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {popularTags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-2 bg-gray-100 text-secondaryTextColor text-sm font-medium rounded-lg hover:bg-brandBlue hover:text-white transition-all duration-300 cursor-pointer"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </motion.div>

                  {/* Recent Posts */}
                  <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    animate={
                      isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }
                    }
                    transition={{ duration: 0.6, delay: 0.6 }}
                    className="bg-white rounded-3xl border border-gray-200 p-6"
                  >
                    <h3 className="text-xl font-bold text-primaryTextColor mb-4 font-lexend">
                      Recent Articles
                    </h3>
                    <div className="space-y-4">
                      {recentPosts.map((post, index) => (
                        <div
                          key={post.id}
                          className="flex items-start space-x-3 group cursor-pointer"
                        >
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center flex-shrink-0">
                            <BookOpen className="w-5 h-5 text-gray-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-primaryTextColor group-hover:text-brandBlue transition-colors duration-300 line-clamp-2">
                              {post.title}
                            </h4>
                            <p className="text-xs text-secondaryTextColor mt-1">
                              {new Date(post.date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>

                  {/* Newsletter Signup */}
                  {/* <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    animate={
                      isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }
                    }
                    transition={{ duration: 0.6, delay: 0.8 }}
                    className="bg-gradient-to-br from-brandBlue to-brandGreen rounded-3xl p-6 text-white"
                  >
                    <h3 className="text-xl font-bold mb-3 font-lexend">
                      Stay Updated
                    </h3>
                    <p className="text-white/80 text-sm mb-4">
                      Get the latest articles and insights delivered to your
                      inbox.
                    </p>
                    <div className="space-y-3">
                      <input
                        type="email"
                        placeholder="Enter your email"
                        className="w-full px-4 py-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl placeholder-white/60 text-white focus:ring-2 focus:ring-white/50 focus:border-transparent"
                      />
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="w-full py-3 bg-white text-brandBlue font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
                      >
                        Subscribe
                      </motion.button>
                    </div>
                  </motion.div> */}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Blogs;